import { useState } from 'react';
import { 
  Plus, 
  MessageSquare, 
  Settings, 
  Trash2,
  AlertTriangle,
  CheckCircle,
  Clock,
  QrCode,
  Phone,
  Shield,
  Info,
  X,
  ChevronRight,
  Loader2
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import { useIntegrations } from '../hooks/useIntegrations';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { PhoneNumberInput } from '../components/PhoneNumberInput';
import { Integration } from '../types';

type ConnectionStep = 'phone' | 'consent' | 'qr' | 'connecting' | 'complete';

export function Integrations() {
  const { state } = useApp();
  const { 
    integrations, 
    loading, 
    createIntegration, 
    deleteIntegration,
    connectWhatsApp 
  } = useIntegrations();
  
  const [showAddModal, setShowAddModal] = useState(false);
  const [connectionStep, setConnectionStep] = useState<ConnectionStep>('phone');
  const [trackedPhoneNumber, setTrackedPhoneNumber] = useState('');
  const [trackedAccountName, setTrackedAccountName] = useState('');
  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const [consentGiven, setConsentGiven] = useState(false);
  const [connecting, setConnecting] = useState<string | null>(null);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [currentIntegrationId, setCurrentIntegrationId] = useState<string | null>(null);
  const [checkingConnection, setCheckingConnection] = useState(false);

  const resetConnectionFlow = () => {
    setConnectionStep('phone');
    setTrackedPhoneNumber('');
    setTrackedAccountName('');
    setIsPhoneValid(false);
    setConsentGiven(false);
    setQrCode(null);
    setCurrentIntegrationId(null);
    setCheckingConnection(false);
  };

  const handleStartConnection = () => {
    setShowAddModal(true);
    resetConnectionFlow();
  };

  const handlePhoneSubmit = () => {
    if (trackedPhoneNumber.trim() && isPhoneValid) {
      setConnectionStep('consent');
    }
  };

  const handlePhoneChange = (phoneNumber: string, isValid: boolean) => {
    setTrackedPhoneNumber(phoneNumber);
    setIsPhoneValid(isValid);
  };

  const handleConsentSubmit = async () => {
    if (!consentGiven || !state.user?.tenant_id) return;
    
    setConnectionStep('connecting');
    
    // Create integration with tracked phone number and name
    const integration = await createIntegration({
      type: 'whatsapp',
      name: trackedAccountName.trim() || undefined,
    });
    
    if (integration) {
      setCurrentIntegrationId(integration.id);
      
      // Initiate WhatsApp connection to get QR code
      const result = await connectWhatsApp(integration.id);
      
      if (result && 'qr_code' in result && result.qr_code) {
        // QR code generated successfully
        setQrCode(result.qr_code as string);
        setConnectionStep('qr');
      } else if (result && result.status === 'active') {
        // Already connected (unlikely on first attempt)
        setConnectionStep('complete');
      } else {
        // Failed to generate QR code
        setConnectionStep('consent');
      }
    } else {
      // Failed to create integration
      setConnectionStep('consent');
    }
  };

  const handleCheckConnection = async () => {
    if (!currentIntegrationId) return;
    
    setCheckingConnection(true);
    
    // Call connectWhatsApp with attemptConnection=true to trigger actual connection
    const result = await connectWhatsApp(currentIntegrationId, true);
    
    if (result && result.status === 'active') {
      // Connection successful!
      setConnectionStep('complete');
    } else if (result && 'qr_code' in result && result.qr_code) {
      // Still pending, QR code may have been refreshed
      setQrCode(result.qr_code as string);
    }
    
    setCheckingConnection(false);
  };

  const handleConnect = async (integration: Integration) => {
    if (integration.type === 'whatsapp') {
      setConnecting(integration.id);
      const result = await connectWhatsApp(integration.id);
      if (result && 'qr_code' in result && result.qr_code) {
        setQrCode(result.qr_code as string);
      }
      setConnecting(null);
    }
  };

  const handleDelete = async (integration: Integration) => {
    if (confirm('Are you sure you want to remove this connected messaging account?')) {
      await deleteIntegration(integration.id);
    }
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    resetConnectionFlow();
  };

  // Note: Removed auto-polling. User must click "I've Scanned the Code" button.
  // This prevents the mock from auto-connecting before QR is displayed.

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="h-5 w-5 text-success-500" />;
      case 'connecting':
        return <Clock className="h-5 w-5 text-warning-500" />;
      case 'error':
        return <AlertTriangle className="h-5 w-5 text-error-500" />;
      default:
        return <Clock className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active':
        return 'Connected';
      case 'connecting':
        return 'Connecting';
      case 'error':
        return 'Connection Error';
      case 'inactive':
        return 'Not Connected';
      default:
        return 'Unknown';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'text-success-700 bg-success-100';
      case 'connecting':
        return 'text-warning-700 bg-warning-100';
      case 'error':
        return 'text-error-700 bg-error-100';
      default:
        return 'text-gray-700 bg-gray-100';
    }
  };

  return (
    <div className="py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Connected Messaging Accounts</h1>
            <p className="mt-1 text-sm text-gray-500">
              Monitor and protect conversations with consent-based tracking
            </p>
          </div>
          <button
            onClick={handleStartConnection}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <Plus className="h-4 w-4 mr-2" />
            Connect Account
          </button>
        </div>

        {/* Integrations List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner size="lg" className="text-primary-600" />
          </div>
        ) : integrations.length === 0 ? (
          <div className="text-center py-12">
            <Shield className="mx-auto h-16 w-16 text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">No connected accounts yet</h3>
            <p className="mt-2 text-sm text-gray-500 max-w-md mx-auto">
              Connect a WhatsApp account to start monitoring conversations. 
              We prioritize privacy and only identify potential threats—not full message access.
            </p>
            <div className="mt-6">
              <button
                onClick={handleStartConnection}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <Plus className="h-4 w-4 mr-2" />
                Connect Your First Account
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {integrations.map((integration) => (
              <div
                key={integration.id}
                className="bg-white overflow-hidden shadow rounded-lg border border-gray-200"
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <MessageSquare className="h-8 w-8 text-green-600" />
                      <div className="ml-3">
                        <h3 className="text-lg font-medium text-gray-900">
                          {integration.name || `${integration.type.charAt(0).toUpperCase() + integration.type.slice(1)} Account`}
                        </h3>
                        <p className="text-sm text-gray-500">
                          {integration.config?.phone_number || 'Not connected'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleDelete(integration)}
                        className="text-gray-400 hover:text-error-600"
                        title="Delete integration"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  <div className="mb-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Status</span>
                      <div className="flex items-center">
                        {getStatusIcon(integration.status)}
                        <span className={`ml-2 inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(integration.status)}`}>
                          {getStatusText(integration.status)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {integration.status === 'inactive' && (
                    <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center mb-2">
                        <QrCode className="h-4 w-4 text-gray-600 mr-2" />
                        <span className="text-sm font-medium text-gray-700">Ready to Connect</span>
                      </div>
                      <p className="text-xs text-gray-600">
                        Click connect to scan QR code and link your WhatsApp account.
                      </p>
                    </div>
                  )}

                  {integration.status === 'error' && (
                    <div className="mb-4 p-3 bg-error-50 rounded-lg">
                      <div className="flex items-center mb-2">
                        <AlertTriangle className="h-4 w-4 text-error-600 mr-2" />
                        <span className="text-sm font-medium text-error-700">Connection Error</span>
                      </div>
                      <p className="text-xs text-error-600">
                        Failed to connect. Please try again.
                      </p>
                    </div>
                  )}

                  <div className="flex space-x-2">
                    {integration.status !== 'active' && (
                      <button
                        onClick={() => handleConnect(integration)}
                        disabled={connecting === integration.id}
                        className="flex-1 inline-flex justify-center items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {connecting === integration.id ? (
                          <LoadingSpinner size="sm" className="text-white mr-2" />
                        ) : (
                          <QrCode className="h-4 w-4 mr-2" />
                        )}
                        Connect
                      </button>
                    )}
                    
                    <button
                      className="px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      title="Settings"
                    >
                      <Settings className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Connection Flow Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Connect Messaging Account</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {connectionStep === 'phone' && 'Enter the phone number to monitor'}
                    {connectionStep === 'consent' && 'Review consent and privacy information'}
                    {connectionStep === 'qr' && 'Scan QR code with WhatsApp'}
                    {connectionStep === 'connecting' && 'Establishing connection...'}
                  </p>
                </div>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              {/* Step Indicator */}
              <div className="px-6 py-4 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className={`flex items-center ${connectionStep === 'phone' ? 'text-primary-600' : 'text-gray-400'}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${connectionStep === 'phone' ? 'bg-primary-600 text-white' : 'bg-gray-200'}`}>
                      1
                    </div>
                    <span className="ml-2 text-sm font-medium">Phone Number</span>
                  </div>
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                  <div className={`flex items-center ${connectionStep === 'consent' ? 'text-primary-600' : 'text-gray-400'}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${connectionStep === 'consent' ? 'bg-primary-600 text-white' : 'bg-gray-200'}`}>
                      2
                    </div>
                    <span className="ml-2 text-sm font-medium">Consent</span>
                  </div>
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                  <div className={`flex items-center ${connectionStep === 'qr' || connectionStep === 'connecting' ? 'text-primary-600' : 'text-gray-400'}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${connectionStep === 'qr' || connectionStep === 'connecting' ? 'bg-primary-600 text-white' : 'bg-gray-200'}`}>
                      3
                    </div>
                    <span className="ml-2 text-sm font-medium">Connect</span>
                  </div>
                </div>
              </div>

              {/* Step 1: Phone Number */}
              {connectionStep === 'phone' && (
                <div className="px-6 py-6">
                  <div className="mb-6">
                    <div className="flex items-start mb-4">
                      <Phone className="h-6 w-6 text-primary-600 mt-1 mr-3 flex-shrink-0" />
                      <div>
                        <h4 className="text-base font-medium text-gray-900 mb-2">
                          Which phone number would you like to monitor?
                        </h4>
                        <p className="text-sm text-gray-600">
                          Enter the phone number of the account you want to track for safety purposes.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mb-6">
                    <label htmlFor="accountName" className="block text-sm font-medium text-gray-700 mb-2">
                      Account Name <span className="text-error-600">*</span>
                    </label>
                    <input
                      type="text"
                      id="accountName"
                      value={trackedAccountName}
                      onChange={(e) => setTrackedAccountName(e.target.value)}
                      placeholder="e.g., John's Account, Sarah's Phone"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                      maxLength={100}
                      required
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Give this account a friendly name to identify it easily
                    </p>
                  </div>

                  <div className="mb-6">
                    <PhoneNumberInput
                      value={trackedPhoneNumber}
                      onChange={handlePhoneChange}
                      label="Phone Number to Monitor"
                      helperText="This is the account that will be monitored for safety"
                      required
                      autoDetectCountry
                    />
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <Info className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
                      <div className="text-sm text-blue-800">
                        <p className="font-medium mb-1">Important Information</p>
                        <p>
                          The account owner will be notified that monitoring is active. 
                          This ensures transparency and maintains trust.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                      onClick={handleCloseModal}
                      className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handlePhoneSubmit}
                      disabled={!isPhoneValid || !trackedAccountName.trim()}
                      className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Continue
                    </button>
                  </div>
                </div>
              )}

              {/* Step 2: Consent */}
              {connectionStep === 'consent' && (
                <div className="px-6 py-6">
                  <div className="mb-6">
                    <div className="flex items-start mb-4">
                      <Shield className="h-6 w-6 text-primary-600 mt-1 mr-3 flex-shrink-0" />
                      <div>
                        <h4 className="text-base font-medium text-gray-900 mb-2">
                          Consent & Privacy Agreement
                        </h4>
                        <p className="text-sm text-gray-600">
                          Please review and acknowledge the following before proceeding.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4 mb-6">
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <h5 className="font-medium text-gray-900 mb-2">User Consent Required</h5>
                      <p className="text-sm text-gray-700 mb-2">
                        By connecting this account, you confirm that:
                      </p>
                      <ul className="text-sm text-gray-700 space-y-1 ml-4 list-disc">
                        <li>You have obtained explicit consent from the account owner (<strong>{trackedPhoneNumber}</strong>)</li>
                        <li>The account owner is aware that their conversations will be monitored</li>
                        <li>The account owner understands the purpose is safety and threat detection</li>
                      </ul>
                    </div>

                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <h5 className="font-medium text-gray-900 mb-2">Privacy Protection</h5>
                      <p className="text-sm text-gray-700 mb-2">
                        Chat Safe prioritizes privacy:
                      </p>
                      <ul className="text-sm text-gray-700 space-y-1 ml-4 list-disc">
                        <li><strong>Limited Access:</strong> We do NOT provide full message content access</li>
                        <li><strong>Threat Detection Only:</strong> Our system identifies potentially unsafe conversations or threats</li>
                        <li><strong>Notification System:</strong> The tracked account will receive a notification about monitoring</li>
                        <li><strong>Transparency:</strong> All monitoring activity is logged and visible to the account owner</li>
                      </ul>
                    </div>

                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <div className="flex items-start">
                        <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
                        <div className="text-sm text-yellow-800">
                          <p className="font-medium mb-1">Legal Responsibility</p>
                          <p>
                            You are responsible for ensuring you have legal authority to monitor this account. 
                            Unauthorized monitoring may violate privacy laws in your jurisdiction.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="flex items-start cursor-pointer">
                      <input
                        type="checkbox"
                        checked={consentGiven}
                        onChange={(e) => setConsentGiven(e.target.checked)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded mt-1"
                      />
                      <span className="ml-3 text-sm text-gray-700">
                        I confirm that I have obtained proper consent from the account owner and understand 
                        the privacy protections in place. I acknowledge that the system will notify the 
                        tracked account and will only identify potential threats, not provide full message access.
                      </span>
                    </label>
                  </div>

                  <div className="flex justify-between">
                    <button
                      onClick={() => setConnectionStep('phone')}
                      className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      Back
                    </button>
                    <button
                      onClick={handleConsentSubmit}
                      disabled={!consentGiven || loading}
                      className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="inline h-4 w-4 mr-2 animate-spin" />
                          Setting up...
                        </>
                      ) : (
                        'Accept & Continue'
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3: QR Code */}
              {connectionStep === 'qr' && (
                <div className="px-6 py-6">
                  <div className="mb-6">
                    <div className="flex items-start mb-4">
                      <QrCode className="h-6 w-6 text-primary-600 mt-1 mr-3 flex-shrink-0" />
                      <div>
                        <h4 className="text-base font-medium text-gray-900 mb-2">
                          Scan QR Code with WhatsApp
                        </h4>
                        <p className="text-sm text-gray-600">
                          Open WhatsApp on the phone you want to monitor and scan this code.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col items-center mb-6">
                    {qrCode ? (
                      <div className="bg-white p-6 rounded-lg border-2 border-gray-200">
                        <img src={qrCode} alt="WhatsApp QR Code" className="w-64 h-64" />
                      </div>
                    ) : (
                      <div className="bg-gray-100 p-6 rounded-lg border-2 border-gray-200 w-64 h-64 flex items-center justify-center">
                        <Loader2 className="h-12 w-12 text-gray-400 animate-spin" />
                      </div>
                    )}
                    <p className="mt-4 text-sm text-gray-600 text-center max-w-md">
                      QR code will refresh automatically if not scanned within 60 seconds
                    </p>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                    <h5 className="font-medium text-gray-900 mb-2">How to scan:</h5>
                    <ol className="text-sm text-gray-700 space-y-1 ml-4 list-decimal">
                      <li>Open WhatsApp on the phone ({trackedPhoneNumber})</li>
                      <li>Tap Menu (⋮) or Settings</li>
                      <li>Tap "Linked Devices"</li>
                      <li>Tap "Link a Device"</li>
                      <li>Point your phone at this screen to scan the code</li>
                    </ol>
                  </div>

                  <div className="flex justify-between">
                    <button
                      onClick={handleCloseModal}
                      className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleCheckConnection}
                      disabled={checkingConnection}
                      className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    >
                      {checkingConnection ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Checking...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="h-4 w-4 mr-2" />
                          I've Scanned the Code
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Connecting State */}
              {connectionStep === 'connecting' && (
                <div className="px-6 py-12 text-center">
                  <Loader2 className="h-16 w-16 text-primary-600 animate-spin mx-auto mb-4" />
                  <h4 className="text-lg font-medium text-gray-900 mb-2">
                    Establishing Connection...
                  </h4>
                  <p className="text-sm text-gray-600">
                    Please wait while we set up the monitoring connection.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
